import { type Theme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { createTheme, darken, lighten, type PaletteOptions } from "@mui/material/styles";

import { fernieColors } from "~/themes/fernieBrand";
import { modulePalettes } from "~/themes/moduleColors";
import trialBrandColors from "~/themes/trialBrand";

function parseColor(color: string): [number, number, number] {
	const rgb = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
	if (rgb) {
		return [parseInt(rgb[1], 10) / 255, parseInt(rgb[2], 10) / 255, parseInt(rgb[3], 10) / 255];
	}
	return [
		parseInt(color.slice(1, 3), 16) / 255,
		parseInt(color.slice(3, 5), 16) / 255,
		parseInt(color.slice(5, 7), 16) / 255
	];
}

function relativeLuminance(rr: number, gg: number, bb: number): number {
	const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
	return 0.2126 * lin(rr) + 0.7152 * lin(gg) + 0.0722 * lin(bb);
}

function toHexChannel(c: number): string {
	return Math.round(c * 255)
		.toString(16)
		.padStart(2, "0");
}

/**
 * Darken a color (hex #rrggbb or rgb(r,g,b)) just enough so white text meets WCAG AA
 * contrast (4.5:1). Colors that already pass are returned unchanged. Always returns hex.
 */
export function ensureContrast(color: string, targetRatio = 4.5): string {
	const [r, g, b] = parseColor(color);
	const lum = relativeLuminance(r, g, b);

	if (1.05 / (lum + 0.05) >= targetRatio) return `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

	// Binary-search for the darkening factor that hits the target ratio
	let lo = 0;
	let hi = 1;
	for (let i = 0; i < 30; i++) {
		const mid = (lo + hi) / 2;
		const dl = relativeLuminance(r * (1 - mid), g * (1 - mid), b * (1 - mid));
		if (1.05 / (dl + 0.05) >= targetRatio) {
			hi = mid;
		} else {
			lo = mid;
		}
	}

	const f = 1 - hi;
	return `#${toHexChannel(r * f)}${toHexChannel(g * f)}${toHexChannel(b * f)}`;
}

const DARK_SURFACE_LUMINANCE = relativeLuminance(...parseColor("#121212"));

export function ensureContrastOnDark(color: string, targetRatio = 4.5): string {
	const [r, g, b] = parseColor(color);
	const lum = relativeLuminance(r, g, b);

	if ((lum + 0.05) / (DARK_SURFACE_LUMINANCE + 0.05) >= targetRatio)
		return `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

	let lo = 0;
	let hi = 1;
	for (let i = 0; i < 30; i++) {
		const mid = (lo + hi) / 2;
		const ll = relativeLuminance(r + (1 - r) * mid, g + (1 - g) * mid, b + (1 - b) * mid);
		if ((ll + 0.05) / (DARK_SURFACE_LUMINANCE + 0.05) >= targetRatio) {
			hi = mid;
		} else {
			lo = mid;
		}
	}

	const f = hi;
	return `#${toHexChannel(r + (1 - r) * f)}${toHexChannel(g + (1 - g) * f)}${toHexChannel(b + (1 - b) * f)}`;
}

// Common color tokens extracted from repeated hex literals
const WHITE = "#ffffff";
const BLACK = "#000000";
const DARK_TEXT = "#333333";
const MID_TEXT = "#555555";
const LIGHT_TEXT = "#777777";

const SURFACE_LIGHT = "#dddddd";
const SURFACE_LIGHTER = "#eeeeee";
const DARK_SURFACE = "#444444";

declare module "@mui/material/styles" {
	interface Palette {
		accent: Palette["primary"];
		accentBlack: Palette["primary"];
		accentBlue: Palette["primary"];
		accentCyan: Palette["primary"];
		accentGreen: Palette["primary"];
		accentIndigo: Palette["primary"];
		accentOrange: Palette["primary"];
		accentPink: Palette["primary"];
		accentPurple: Palette["primary"];
		accentRed: Palette["primary"];
		accentTeal: Palette["primary"];
		accentYellow: Palette["primary"];
		headerCard: Palette["primary"];
		plain: Palette["primary"];
		selectCard: Palette["primary"];
	}

	interface PaletteOptions {
		accent: PaletteOptions["primary"];
		accentBlack: PaletteOptions["primary"];
		accentBlue: PaletteOptions["primary"];
		accentCyan: PaletteOptions["primary"];
		accentGreen: PaletteOptions["primary"];
		accentIndigo: PaletteOptions["primary"];
		accentOrange: PaletteOptions["primary"];
		accentPink: PaletteOptions["primary"];
		accentPurple: PaletteOptions["primary"];
		accentRed: PaletteOptions["primary"];
		accentTeal: PaletteOptions["primary"];
		accentYellow: PaletteOptions["primary"];
		headerCard: PaletteOptions["primary"];
		plain: PaletteOptions["primary"];
		selectCard: PaletteOptions["primary"];
	}
}

declare module "@mui/material/Icon" {
	interface IconPropsColorOverrides {
		accentBlack: true;
		accentBlue: true;
		accentCyan: true;
		accentGreen: true;
		accentIndigo: true;
		accentOrange: true;
		accentPink: true;
		accentPurple: true;
		accentRed: true;
		accentTeal: true;
		accentYellow: true;
		headerCard: true;
		plain: true;
		selectCard: true;
	}
}
declare module "@mui/material/Chip" {
	interface ChipPropsColorOverrides {
		accentBlack: true;
		accentBlue: true;
		accentCyan: true;
		accentGreen: true;
		accentIndigo: true;
		accentOrange: true;
		accentPink: true;
		accentPurple: true;
		accentRed: true;
		accentTeal: true;
		accentYellow: true;
	}
}

declare module "@mui/material/Button" {
	interface ButtonPropsColorOverrides {
		accentBlack: true;
		accentBlue: true;
		accentCyan: true;
		accentGreen: true;
		accentIndigo: true;
		accentOrange: true;
		accentPink: true;
		accentPurple: true;
		accentRed: true;
		accentTeal: true;
		accentYellow: true;
	}
}

declare module "@mui/material/ToggleButtonGroup" {
	interface ToggleButtonGroupPropsColorOverrides {
		accentBlack: true;
		accentBlue: true;
		accentCyan: true;
		accentGreen: true;
		accentIndigo: true;
		accentOrange: true;
		accentPink: true;
		accentPurple: true;
		accentRed: true;
		accentTeal: true;
		accentYellow: true;
	}
}

let fontSize = 14;

interface ApplicationPaletteModes {
	dark: Partial<PaletteOptions>;
	light: Partial<PaletteOptions>;
}

interface ApplicationPaletteMap {
	[key: string]: ApplicationPaletteModes;
}

const applicationPalleteMap: ApplicationPaletteMap = {
	availability: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.AVAILABILITY.primary)
			},
			secondary: {
				main: lighten(modulePalettes.AVAILABILITY.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.AVAILABILITY.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.AVAILABILITY.primary)
			},
			secondary: {
				main: modulePalettes.AVAILABILITY.desat1
			},
			selectCard: {
				main: modulePalettes.AVAILABILITY.desat2
			}
		}
	},
	benchmark: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrastOnDark(modulePalettes.BENCHMARK.primary)
			},
			secondary: {
				main: lighten(modulePalettes.BENCHMARK.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.BENCHMARK.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.BENCHMARK.primary)
			},
			secondary: {
				main: modulePalettes.BENCHMARK.desat1
			},
			selectCard: {
				main: modulePalettes.BENCHMARK.desat2
			}
		}
	},
	count: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrastOnDark(modulePalettes.COUNT.primary)
			},
			secondary: {
				main: lighten(modulePalettes.COUNT.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.COUNT.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.COUNT.primary)
			},
			secondary: {
				main: modulePalettes.COUNT.desat1
			},
			selectCard: {
				main: modulePalettes.COUNT.desat2
			}
		}
	},
	engage: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.ENGAGE.primary)
			},
			secondary: {
				main: lighten(modulePalettes.ENGAGE.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.ENGAGE.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.ENGAGE.primary)
			},
			secondary: {
				main: modulePalettes.ENGAGE.desat1
			},
			selectCard: {
				main: modulePalettes.ENGAGE.desat2
			}
		}
	},
	events: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.EVENTS.primary)
			},
			secondary: {
				main: lighten(modulePalettes.EVENTS.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.EVENTS.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.EVENTS.primary)
			},
			secondary: {
				main: modulePalettes.EVENTS.desat1
			},
			selectCard: {
				main: modulePalettes.EVENTS.desat2
			}
		}
	},
	forecast: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.FORECAST.primary)
			},
			secondary: {
				main: lighten(modulePalettes.FORECAST.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.FORECAST.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.FORECAST.primary)
			},
			secondary: {
				main: modulePalettes.FORECAST.desat1
			},
			selectCard: {
				main: modulePalettes.FORECAST.desat2
			}
		}
	},
	fulfill: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.FULFILL.primary)
			},
			secondary: {
				main: lighten(modulePalettes.FULFILL.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.FULFILL.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.FULFILL.primary)
			},
			secondary: {
				main: modulePalettes.FULFILL.desat1
			},
			selectCard: {
				main: modulePalettes.FULFILL.desat2
			}
		}
	},
	intelligence: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.INTELLIGENCE.primary)
			},
			secondary: {
				main: lighten(modulePalettes.INTELLIGENCE.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.INTELLIGENCE.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.INTELLIGENCE.primary)
			},
			secondary: {
				main: modulePalettes.INTELLIGENCE.desat1
			},
			selectCard: {
				main: modulePalettes.INTELLIGENCE.desat2
			}
		}
	},
	material: {
		dark: {
			mode: "dark",
			primary: {
				contrastText: BLACK,
				dark: ensureContrastOnDark(modulePalettes.MATERIAL.primary, 5.5),
				main: ensureContrastOnDark(modulePalettes.MATERIAL.primary)
			},
			secondary: {
				main: lighten(modulePalettes.MATERIAL.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.MATERIAL.primary, 0.86)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.MATERIAL.primary)
			},
			secondary: {
				main: modulePalettes.MATERIAL.darker
			},
			selectCard: {
				main: modulePalettes.MATERIAL.desat2
			}
		}
	},
	portal: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.PORTAL.primary)
			},
			secondary: {
				main: lighten(modulePalettes.PORTAL.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.PORTAL.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.PORTAL.primary)
			},
			secondary: {
				main: modulePalettes.PORTAL.desat1
			},
			selectCard: {
				main: modulePalettes.PORTAL.desat2
			}
		}
	},
	produce: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.PRODUCE.primary)
			},
			secondary: {
				main: lighten(modulePalettes.PRODUCE.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.PRODUCE.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.PRODUCE.primary)
			},
			secondary: {
				main: modulePalettes.PRODUCE.desat1
			},
			selectCard: {
				main: modulePalettes.PRODUCE.desat2
			}
		}
	},
	reports: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.REPORTS.primary)
			},
			secondary: {
				main: lighten(modulePalettes.REPORTS.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.REPORTS.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.REPORTS.primary)
			},
			secondary: {
				main: modulePalettes.REPORTS.desat1
			},
			selectCard: {
				main: modulePalettes.REPORTS.desat2
			}
		}
	},
	restock: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.RESTOCK.primary)
			},
			secondary: {
				main: modulePalettes.RESTOCK.darker
			},
			selectCard: {
				main: darken(modulePalettes.RESTOCK.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.RESTOCK.primary)
			},
			secondary: {
				main: modulePalettes.RESTOCK.darker
			},
			selectCard: {
				main: modulePalettes.RESTOCK.desat2
			}
		}
	},
	verify: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.VERIFY.primary)
			},
			secondary: {
				main: lighten(modulePalettes.VERIFY.primary, 0.2)
			},
			selectCard: {
				main: darken(modulePalettes.VERIFY.primary, 0.66)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.VERIFY.primary)
			},
			secondary: {
				main: modulePalettes.VERIFY.desat1
			},
			selectCard: {
				main: modulePalettes.VERIFY.desat2
			}
		}
	},
	worksuite: {
		dark: {
			mode: "dark",
			primary: {
				main: ensureContrast(modulePalettes.WORKSUITE.primary)
			},
			secondary: {
				main: ensureContrast(lighten(modulePalettes.WORKSUITE.primary, 0.4))
			},
			selectCard: {
				main: darken(modulePalettes.WORKSUITE.primary, 0.86)
			}
		},
		light: {
			primary: {
				main: ensureContrast(modulePalettes.WORKSUITE.primary)
			},
			secondary: {
				main: ensureContrast(lighten(modulePalettes.WORKSUITE.primary, 0.4))
			},
			selectCard: {
				main: modulePalettes.WORKSUITE.desat2
			}
		}
	}
};

const headerCard = {
	dark: {
		contrastText: SURFACE_LIGHTER,
		dark: grey[900],
		light: grey[700],
		main: grey[800]
	},
	light: {
		contrastText: DARK_TEXT,
		dark: grey[200],
		light: grey[50],
		main: grey[100]
	}
};

const selectCard = {
	dark: {
		contrastText: WHITE,
		dark: DARK_TEXT,
		light: MID_TEXT,
		main: DARK_SURFACE
	},
	light: {
		contrastText: DARK_TEXT,
		dark: SURFACE_LIGHT,
		light: WHITE,
		main: "#f5f5f5"
	}
};

const plain = {
	dark: {
		contrastText: WHITE,
		dark: DARK_SURFACE,
		light: "#666666",
		main: MID_TEXT
	},
	light: {
		contrastText: SURFACE_LIGHT,
		dark: DARK_TEXT,
		light: LIGHT_TEXT,
		main: MID_TEXT
	}
};

const accentBlack = {
	dark: {
		contrastText: WHITE,
		dark: BLACK,
		light: DARK_TEXT,
		main: BLACK
	},
	light: {
		contrastText: WHITE,
		dark: BLACK,
		light: DARK_TEXT,
		main: BLACK
	}
};

const accentBlue = {
	dark: {
		contrastText: WHITE,
		dark: "#1565C0",
		light: "#64B5F6",
		main: "#2196F3"
	},
	light: {
		contrastText: WHITE,
		dark: "#1976D2",
		light: "#90CAF9",
		main: "#42A5F5"
	}
};

const accentGreen = {
	dark: {
		contrastText: WHITE,
		dark: "#2E7D32",
		light: "#81C784",
		main: "#4CAF50"
	},
	light: {
		contrastText: WHITE,
		dark: "#388E3C",
		light: "#A5D6A7",
		main: "#66BB6A"
	}
};

const accentRed = {
	dark: {
		contrastText: WHITE,
		dark: "#C62828",
		light: "#EF5350",
		main: "#F44336"
	},
	light: {
		contrastText: WHITE,
		dark: "#D32F2F",
		light: "#FF6659",
		main: "#EF5350"
	}
};

const accentPurple = {
	dark: {
		contrastText: WHITE,
		dark: "#6A1B9A",
		light: "#BA68C8",
		main: "#9C27B0"
	},
	light: {
		contrastText: WHITE,
		dark: "#7B1FA2",
		light: "#CE93D8",
		main: "#AB47BC"
	}
};

const accentOrange = {
	dark: {
		contrastText: WHITE,
		dark: "#D84315",
		light: "#FF8A65",
		main: "#eb6123"
	},
	light: {
		contrastText: WHITE,
		dark: "#BF360C",
		light: "#FFAB91",
		main: "#EB6123"
	}
};

const accentTeal = {
	dark: {
		contrastText: WHITE,
		dark: "#00695C",
		light: "#4DB6AC",
		main: "#009688"
	},
	light: {
		contrastText: WHITE,
		dark: "#00796B",
		light: "#80CBC4",
		main: "#26A69A"
	}
};

const accentPink = {
	dark: {
		contrastText: WHITE,
		dark: "#AD1457",
		light: "#F06292",
		main: "#E91E63"
	},
	light: {
		contrastText: WHITE,
		dark: "#C2185B",
		light: "#F48FB1",
		main: "#EC407A"
	}
};

const accentYellow = {
	dark: {
		contrastText: BLACK,
		dark: "#FBC02D",
		light: "#FFF176",
		main: "#FFEB3B"
	},
	light: {
		contrastText: BLACK,
		dark: "#FDD835",
		light: "#FFF59D",
		main: "#FFEE58"
	}
};

const accentCyan = {
	dark: {
		contrastText: WHITE,
		dark: "#00838F",
		light: "#4DD0E1",
		main: "#00BCD4"
	},
	light: {
		contrastText: WHITE,
		dark: "#0097A7",
		light: "#80DEEA",
		main: "#26C6DA"
	}
};

const accentIndigo = {
	dark: {
		contrastText: WHITE,
		dark: "#283593",
		light: "#7986CB",
		main: "#3F51B5"
	},
	light: {
		contrastText: WHITE,
		dark: "#303F9F",
		light: "#9FA8DA",
		main: "#5C6BC0"
	}
};

// background and text: MUI defaults (no overrides).
// Dark: paper #121212, text.primary rgba(255,255,255,0.87)
// Light: paper #fff, text.primary rgba(0,0,0,0.87)

declare module "@mui/material/styles" {
	interface TypographyVariants {
		brandFont: string;
		dataGrid: React.CSSProperties;
	}

	interface TypographyVariantsOptions {
		brandFont?: string;
		dataGrid?: React.CSSProperties;
	}
}

declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		dataGrid: true;
	}
}

declare module "@mui/material/Button" {
	interface ButtonPropsColorOverrides {
		plain: true;
	}
}

declare module "@mui/material/ToggleButtonGroup" {
	interface ToggleButtonGroupPropsColorOverrides {
		plain: true;
	}
}

export function themeCreator(
	theme: string,
	type: string,
	defaultFontSize?: string | null,
	paletteOverride?: Partial<PaletteOptions>
): Theme {
	fontSize = Number(defaultFontSize) || fontSize;
	const themeType = (type === "dark" ? "dark" : "light") as keyof ApplicationPaletteModes;
	const basePalette = applicationPalleteMap[theme]
		? applicationPalleteMap[theme][themeType]
		: applicationPalleteMap["worksuite"][themeType];
	const palette = paletteOverride ? { ...basePalette, ...paletteOverride } : basePalette;
	const components = {
		MuiCard: {
			styleOverrides: {
				root: {
					backgroundColor: palette.selectCard && "main" in palette.selectCard ? palette.selectCard.main : WHITE
				}
			}
		},
		MuiContainer: {
			styleOverrides: {
				maxWidthXl: {
					"@media (min-width: 1920px)": {
						maxWidth: "1800px",
						width: "100%"
					}
				},
				root: {
					width: "unset"
				}
			}
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					"@media (max-width: 600px)": {
						padding: ".5em"
					}
				}
			}
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					"@media (max-width: 600px)": {
						padding: ".5em"
					}
				}
			}
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					"@media (max-width: 600px)": {
						padding: ".5em"
					}
				}
			}
		},
		MuiIcon: {
			defaultProps: {
				baseClassName: "material-symbols-rounded"
			},
			styleOverrides: {
				root: ({ ownerState, theme }: { ownerState: { color?: string }; theme: Theme }) => ({
					...(ownerState.color === "headerCard" && {
						color: theme.palette.headerCard.main
					}),
					...(ownerState.color === "plain" && {
						color: theme.palette.plain.main
					}),
					...(ownerState.color === "selectCard" && {
						color: theme.palette.selectCard.main
					}),
					...(ownerState.color === "accentBlack" && {
						color: theme.palette.accentBlack.main
					}),
					...(ownerState.color === "accentBlue" && {
						color: theme.palette.accentBlue.main
					}),
					...(ownerState.color === "accentGreen" && {
						color: theme.palette.accentGreen.main
					}),
					...(ownerState.color === "accentRed" && {
						color: theme.palette.accentRed.main
					}),
					...(ownerState.color === "accentPurple" && {
						color: theme.palette.accentPurple.main
					}),
					...(ownerState.color === "accentOrange" && {
						color: theme.palette.accentOrange.main
					}),
					...(ownerState.color === "accentTeal" && {
						color: theme.palette.accentTeal.main
					}),
					...(ownerState.color === "accentPink" && {
						color: theme.palette.accentPink.main
					}),
					...(ownerState.color === "accentYellow" && {
						color: theme.palette.accentYellow.main
					}),
					...(ownerState.color === "accentCyan" && {
						color: theme.palette.accentCyan.main
					}),
					...(ownerState.color === "accentIndigo" && {
						color: theme.palette.accentIndigo.main
					})
				})
			}
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					"@media (max-width: 900px)": {
						padding: 0
					}
				}
			}
		}
	};

	const FONT_FAMILY_INTER = "'Inter', sans-serif";
	const FONT_FAMILY_JETBRAINS = "'JetBrains Mono', monospace";
	const FONT_FAMILY_MONTSERRAT = "'Montserrat', sans-serif";

	return createTheme({
		components: components,
		palette: {
			...palette,
			accent: palette.accent || palette.primary,
			accentBlack: accentBlack[themeType],
			accentBlue: accentBlue[themeType],
			accentCyan: accentCyan[themeType],
			accentGreen: accentGreen[themeType],
			accentIndigo: accentIndigo[themeType],
			accentOrange: accentOrange[themeType],
			accentPink: accentPink[themeType],
			accentPurple: accentPurple[themeType],
			accentRed: accentRed[themeType],
			accentTeal: accentTeal[themeType],
			accentYellow: accentYellow[themeType],
			fernie: fernieColors,
			headerCard: headerCard[themeType],
			plain: plain[themeType],
			selectCard: palette.selectCard || selectCard[themeType],
			trialBrand: trialBrandColors
		},
		typography: {
			body1: {
				fontSize: "14px",
				fontWeight: 400,
				lineHeight: 1.5
			},
			body2: {
				fontSize: "13px",
				fontWeight: 400,
				lineHeight: 1.5
			},
			brandFont: FONT_FAMILY_MONTSERRAT,
			caption: {
				fontSize: "12px",
				fontWeight: 400,
				lineHeight: 1.4
			},
			dataGrid: {
				fontFamily: FONT_FAMILY_JETBRAINS,
				fontSize: "13px",
				fontVariantNumeric: "tabular-nums",
				fontWeight: 400
			},
			fontFamily: FONT_FAMILY_INTER,
			fontSize: fontSize,
			h4: {
				fontSize: "28px",
				fontWeight: 600,
				lineHeight: 1.2
			},
			h5: {
				fontSize: "20px",
				fontWeight: 600,
				lineHeight: 1.3
			},
			h6: {
				fontSize: "14px",
				fontWeight: 600,
				lineHeight: 1.4
			}
		}
	});
}
